import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { useTeacherContext } from "@/lib/teacher-context";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Save, BarChart3 } from "lucide-react";

export const Route = createFileRoute("/teacher/assessments")({
  ssr: false,
  beforeLoad: () => requireRole("teacher"),
  head: () => ({ meta: [{ title: "Assessments — MAKAI" }] }),
  component: AssessmentsPage,
});

function AssessmentsPage() {
  const ctx = useTeacherContext();
  const [items, setItems] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [marking, setMarking] = useState<any | null>(null);

  const load = async () => {
    if (!ctx.staffId) return;
    const { data } = await supabase
      .from("assessments")
      .select("id,title,kind,max_score,weight,assessed_on,status,stream_id,subject_id,stream:streams(name,class:classes(name)),subject:subjects_catalog(name)")
      .eq("teacher_id", ctx.staffId).order("assessed_on", { ascending: false, nullsFirst: false });
    setItems(data ?? []);
  };

  useEffect(() => { if (!ctx.loading) load(); }, [ctx.loading, ctx.staffId]);

  return (
    <AppShell role="teacher" title="Assessments" subtitle="Quizzes, tests and exams with marks and analytics."
      action={<Button onClick={() => setOpen(true)}><Plus className="mr-1 h-4 w-4" /> New assessment</Button>}>
      {items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">No assessments yet.</div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {items.map((a) => (
            <div key={a.id} className="rounded-2xl border border-border bg-card p-4 shadow-soft">
              <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-2">
                <div className="min-w-0">
                  <div className="truncate font-display font-bold">{a.title}</div>
                  <div className="text-xs text-muted-foreground capitalize">{a.kind} · {a.stream?.class?.name} · {a.stream?.name} · {a.subject?.name}</div>
                </div>
                <span className="rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase text-gold-foreground">{a.kind}</span>
              </div>
              <div className="mt-2 text-xs text-muted-foreground">Out of {a.max_score} · {a.assessed_on ? new Date(a.assessed_on).toLocaleDateString() : "Unscheduled"}</div>
              <div className="mt-3 flex justify-end">
                <Button size="sm" onClick={() => setMarking(a)}><BarChart3 className="mr-1 h-4 w-4" /> Enter marks</Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <AssessmentDialog open={open} onOpenChange={setOpen} ctx={ctx} onSaved={load} />
      {marking && <MarksDialog assessment={marking} onClose={() => { setMarking(null); load(); }} />}
    </AppShell>
  );
}

function AssessmentDialog({ open, onOpenChange, ctx, onSaved }: any) {
  const [form, setForm] = useState<any>({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      const sid = ctx.streams[0]?.id ?? "";
      setForm({
        title: "", kind: "quiz", max_score: 100, weight: 1, assessed_on: "", status: "open",
        stream_id: sid,
        subject_id: ctx.assignments.filter((a: any) => a.stream.id === sid && a.subject)[0]?.subject?.id ?? "",
      });
    }
  }, [open, ctx]);
  const subjectsForStream = ctx.assignments.filter((a: any) => a.stream.id === form.stream_id && a.subject).map((a: any) => a.subject);

  async function save() {
    if (!form.title || !form.stream_id || !form.subject_id) { toast.error("Title, class and subject required"); return; }
    setSaving(true);
    const { error } = await supabase.from("assessments").insert({
      ...form, school_id: ctx.schoolId, teacher_id: ctx.staffId,
      assessed_on: form.assessed_on || null,
      max_score: Number(form.max_score) || 100, weight: Number(form.weight) || 1,
    });
    setSaving(false);
    if (error) toast.error(error.message); else { toast.success("Created"); onOpenChange(false); onSaved(); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle>New assessment</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <Input placeholder="Title" value={form.title ?? ""} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <div className="grid gap-3 sm:grid-cols-2">
            <select value={form.stream_id ?? ""} onChange={(e) => setForm({ ...form, stream_id: e.target.value, subject_id: "" })} className="rounded-lg border border-input bg-background px-3 py-2 text-sm">
              {ctx.streams.map((s: any) => <option key={s.id} value={s.id}>{s.class_name} · {s.name}</option>)}
            </select>
            <select value={form.subject_id ?? ""} onChange={(e) => setForm({ ...form, subject_id: e.target.value })} className="rounded-lg border border-input bg-background px-3 py-2 text-sm">
              <option value="">Subject…</option>
              {subjectsForStream.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div className="grid gap-3 sm:grid-cols-4">
            <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="rounded-lg border border-input bg-background px-3 py-2 text-sm">
              {["quiz","test","midterm","exam","project"].map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
            <Input type="number" placeholder="Max" value={form.max_score} onChange={(e) => setForm({ ...form, max_score: e.target.value })} />
            <Input type="number" placeholder="Weight" step="0.1" value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })} />
            <Input type="date" value={form.assessed_on ?? ""} onChange={(e) => setForm({ ...form, assessed_on: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving}>{saving ? "Saving…" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MarksDialog({ assessment, onClose }: { assessment: any; onClose: () => void }) {
  const [rows, setRows] = useState<any[]>([]);
  const [edits, setEdits] = useState<Record<string, { score: string; remarks: string }>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const { data: learners } = await supabase.from("learners").select("id, first_name, last_name").eq("current_stream_id", assessment.stream_id).eq("status","active").order("first_name");
      const { data: marks } = await supabase.from("assessment_marks").select("learner_id, score, remarks").eq("assessment_id", assessment.id);
      const byL = new Map((marks ?? []).map((m: any) => [m.learner_id, m]));
      setRows(learners ?? []);
      const e: any = {};
      for (const l of learners ?? []) { const m = byL.get(l.id); e[l.id] = { score: m?.score ?? "", remarks: m?.remarks ?? "" }; }
      setEdits(e);
    })();
  }, [assessment.id, assessment.stream_id]);

  const stats = useMemo(() => {
    const scores = Object.values(edits).map((e) => Number(e.score)).filter((n) => Number.isFinite(n));
    if (!scores.length) return null;
    const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
    const pct = (avg / Number(assessment.max_score || 100)) * 100;
    return { count: scores.length, avg: avg.toFixed(1), pct: pct.toFixed(0), max: Math.max(...scores), min: Math.min(...scores) };
  }, [edits, assessment.max_score]);

  async function saveAll() {
    setSaving(true);
    const { data: a } = await supabase.from("assessments").select("school_id").eq("id", assessment.id).single();
    if (!a?.school_id) { setSaving(false); toast.error("School not found"); return; }
    const ups = rows.map((r) => ({
      assessment_id: assessment.id as string, learner_id: r.id as string, school_id: a.school_id as string,
      score: edits[r.id]?.score === "" ? null : Number(edits[r.id]?.score),
      remarks: edits[r.id]?.remarks || null,
    }));
    const { error } = await supabase.from("assessment_marks").upsert(ups, { onConflict: "assessment_id,learner_id" } as any);
    setSaving(false);
    if (error) toast.error(error.message); else toast.success("Marks saved");
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{assessment.title}</DialogTitle>
          <div className="text-xs text-muted-foreground">Out of {assessment.max_score}</div>
        </DialogHeader>
        {stats && (
          <div className="grid grid-cols-4 gap-2">
            {[["Entered", stats.count], ["Avg", stats.avg], ["High", stats.max], ["Low", stats.min]].map(([k,v]) => (
              <div key={k as string} className="rounded-xl bg-muted p-2 text-center">
                <div className="text-[10px] uppercase text-muted-foreground">{k}</div>
                <div className="font-display text-lg font-bold">{v as any}</div>
              </div>
            ))}
          </div>
        )}
        <div className="overflow-hidden rounded-xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/60 text-left text-xs uppercase text-muted-foreground">
              <tr><th className="px-3 py-2">Learner</th><th className="px-3 py-2 w-24">Score</th><th className="px-3 py-2">Remarks</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border">
                  <td className="px-3 py-2 font-medium">{r.first_name} {r.last_name}</td>
                  <td className="px-3 py-2"><Input value={edits[r.id]?.score ?? ""} onChange={(e) => setEdits({ ...edits, [r.id]: { ...edits[r.id], score: e.target.value } })} className="h-8" /></td>
                  <td className="px-3 py-2"><Input value={edits[r.id]?.remarks ?? ""} onChange={(e) => setEdits({ ...edits, [r.id]: { ...edits[r.id], remarks: e.target.value } })} className="h-8" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={saveAll} disabled={saving}><Save className="mr-1 h-4 w-4" /> {saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
